import { type AxiosInstance } from 'axios';
import * as Sentry from '@sentry/react';
import { startLoading, stopLoading } from '../components/ui/GlobalLoadingBar';
import { config } from '../config/runtime';
import { sanitizeTelemetryData } from '../utils/telemetrySanitizer';
import { createCircuitBreakerError } from './apiErrors';
import { reportApiError } from './errorReportingService';
import { emitCircuitBreakerEvent } from '@/hooks/useServerRecovery';
import { dispatchApiErrorEvent } from '@/hooks/useUserActionTrail';
import { createHttpClient } from './axiosUtils';
import {
  browserApiLogger,
  browserNavigationAdapter,
  browserTenantContextAdapter,
  browserTokenStorageAdapter,
} from './adapters/browserApiAdapters';
import {
  createBaseApiClient,
  type HttpClientConfig,
  type HttpClientInstance,
  type ManagedApiClient,
  type ServerErrorReportArgs,
} from '../../../shared/services/baseApiClient';

const API_BASE_URL = config.API_BASE_URL;
const BASE_DOMAIN = API_BASE_URL.replace(/\/api\/v1\/?$/, '');

const createAxiosClient = (clientConfig: HttpClientConfig): HttpClientInstance =>
  createHttpClient(clientConfig) as unknown as HttpClientInstance;

const reportBrowserServerError = ({
  error,
  friendlyMessage,
  request,
  responseData,
  status,
}: ServerErrorReportArgs): void => {
  try {
    Sentry.captureException(error);
  } catch (captureError) {
    browserApiLogger.debug?.('Sentry captureException failed in error interceptor', {
      captureError,
    });
  }

  // Emit to the action trail ring buffer so bug reports get context
  dispatchApiErrorEvent(status, request?.url ?? '');

  reportApiError(
    request?.url || 'unknown',
    request?.method || 'unknown',
    status,
    friendlyMessage,
    sanitizeTelemetryData(responseData)
  );
};

const createFrontendClient = (baseURL: string, clientName: string, withLoading: boolean) =>
  createBaseApiClient({
    clientName,
    createClient: createAxiosClient,
    baseURL,
    timeout: 30_000,
    defaultHeaders: {
      'Content-Type': 'application/json',
    },
    // JWT auth sends the token via the Authorization header — session cookies
    // are not needed and actively harmful: Django's SessionMiddleware resolves
    // the session user at middleware time (before DRF runs) and the tenant
    // middleware uses that session user for membership checks, causing 403s
    // when the session user's tenant differs from the JWT user's tenant.
    withCredentials: false,
    tokenStorage: browserTokenStorageAdapter,
    tenantContext: browserTenantContextAdapter,
    navigation: browserNavigationAdapter,
    logger: browserApiLogger,
    loadingHooks: withLoading
      ? {
          onRequestStart: startLoading,
          onRequestEnd: stopLoading,
        }
      : undefined,
    circuitBreaker: {
      onTrip: emitCircuitBreakerEvent,
      createError: createCircuitBreakerError,
      report: reportBrowserServerError,
    },
  });

const primaryApi = createFrontendClient(API_BASE_URL, 'API', true);
const adminApi = createFrontendClient(BASE_DOMAIN, 'Admin API', false);

/** Shared browser API client for tenant-scoped application traffic. */
export const apiClient = primaryApi.client as unknown as AxiosInstance;
/** Managed tenant-aware API client for shared React Query hooks. */
export const managedApiClient: ManagedApiClient = primaryApi;
/** Shared browser API client for admin endpoints that live outside `/api/v1`. */
export const adminClient = adminApi.client as unknown as AxiosInstance;

/** Resets shared auth-refresh queue state for focused interceptor tests. */
export const __resetAuthRefreshStateForTests = (): void => {
  primaryApi.resetAuthRefreshState();
  adminApi.resetAuthRefreshState();
};
